import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Button, Card, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Enrollment } from './models/Enrollment';
import TopNavActions from './TopNavActions';
import ConfirmExitDialog from './ConfirmExitDialog';
import { voiceDisorders, neurologicalDisorders, psychiatricDisorders, respiratoryDisorders, cohortNames } from './models/Conditions';

interface Props {
  enrollment: Enrollment;
  onPageChange: (page: string) => void;
}

const disorderOptions = [
  voiceDisorders,
  respiratoryDisorders,
  neurologicalDisorders,
  psychiatricDisorders
];

const EligibilityCriteriaConditionsView: React.FC<Props> = ({ enrollment, onPageChange }) => {
  // State for each cohort's selected options
  const [selected, setSelected] = useState<string[][]>([[], [], [], []]);
  const [openExitDialog, setOpenExitDialog] = React.useState(false);
  const { t } = useTranslation('enrollment');

  useEffect(() => {
    setSelected([
      enrollment?.disorderInformation?.selectedVoiceDisorders ?? [],
      enrollment?.disorderInformation?.selectedRespiratoryDisorders ?? [],
      enrollment?.disorderInformation?.selectedNeurologicalDisorders ?? [],
      enrollment?.disorderInformation?.selectedPsychiatricDisorders ?? []
    ]);
    // eslint-disable-next-line
  }, [enrollment]);

  // Handle checkbox change for a cohort
  const handleOptionChange = (cohortIdx: number, option: string) => {
    setSelected(prev => {
      const newSelected = [...prev];
      newSelected[cohortIdx] = newSelected[cohortIdx].includes(option)
        ? newSelected[cohortIdx].filter(o => o !== option)
        : [...newSelected[cohortIdx], option];
      return newSelected;
    });
  };

  // Save and navigate
  const handleNext = () => {
    const anyDiagnosedDisorder = selected.some(arr => arr.length > 0);
    const isControlParticipant = !anyDiagnosedDisorder;
    enrollment.isControlParticipant = isControlParticipant;
    enrollment.disorderInformation = {
      selectedVoiceDisorders: selected[0],
      selectedRespiratoryDisorders: selected[1],
      selectedNeurologicalDisorders: selected[2],
      selectedPsychiatricDisorders: selected[3],
      selectedPediatricDisorders: []
    };
    enrollment.eligibleStudies = isControlParticipant
      ? cohortNames
      : cohortNames.filter((_: string, idx: number) => selected[idx].length > 0);
    onPageChange('eligible-studies-view');
  };

  const handleBack = () => {
    onPageChange('back');
  };

  // Render a cohort card
  const renderCohortCard = (cohort: string, cohortIdx: number) => (
    <Card key={cohort + '-' + cohortIdx} style={{padding: '1rem', marginBottom: '1rem'}}>
      <FormControl component="fieldset" variant="standard" fullWidth>
        <FormLabel component="legend">
          <Typography variant="h6" component='div'>{t(cohort)}</Typography>
        </FormLabel>
        <FormGroup>
          {disorderOptions[cohortIdx].map((option: string, optionIdx: number) => (
            <FormControlLabel
              key={option + '-' + optionIdx}
              control={
                <Checkbox
                  checked={selected[cohortIdx].includes(option)}
                  onChange={() => handleOptionChange(cohortIdx, option)}
                />
              }
              label={t(option)}
            />
          ))}
        </FormGroup>
      </FormControl>
    </Card>
  );

  return (
    <React.Fragment>
      <TopNavActions
        onBack={handleBack}
        onExit={() => setOpenExitDialog(true)}
      />
      <ConfirmExitDialog
        open={openExitDialog}
        onClose={() => setOpenExitDialog(false)}
        onExit={() => { window.location.href = '/'; }}
      />

      <Typography variant="h4" component='h1' id="page-heading" tabIndex={-1} sx={{mb: 2}}>{t("Tell us about yourself")}</Typography>
      <Typography variant="h6" component='div'>
        {t("Do you have any of these conditions, diagnosed by a clinician?")}
        {t("(Check all that apply if you currently have the condition)")}
      </Typography>
      {cohortNames.map((cohort: string, cohortIdx: number) => (
        renderCohortCard(cohort, cohortIdx)
      ))}
      <Button variant="contained" onClick={handleNext} style={{ marginTop: 32, marginBottom: 32 }}>
        {t("Next")}
      </Button>
    </React.Fragment>
  );
};

export default EligibilityCriteriaConditionsView;
